import React, { useState } from "react";
import { Link, useNavigate } from "react-router";
import { usePost } from "../../api";
import {
  IKindredCouponDetail,
  KindredPublishStatus,
  KindredCouponCoverage,
} from "../../interfaces";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { ArrowLeft, Plus } from "react-feather";
import {
  BrandTypeahead,
  DEFAULT_MANUAL_SOURCE,
  SourcePicker,
  TagListEditor,
} from "../../components/workbench";

const publishedStatusOptions = [
  { value: KindredPublishStatus.Draft, label: "Draft" },
  { value: KindredPublishStatus.AwaitingApproval, label: "Awaiting Approval" },
  { value: KindredPublishStatus.Approved, label: "Approved" },
  { value: KindredPublishStatus.Deactivated, label: "Deactivated" },
];

export const KindredCouponCreate: React.FC = () => {
  const navigate = useNavigate();
  const createCoupon = usePost<IKindredCouponDetail, any>();

  const [form, setForm] = useState({
    code: "",
    brandId: "",
    brandName: "",
    summary: "",
    validFrom: "",
    validTo: "",
    publishedStatus: KindredPublishStatus.Draft,
    coverage: KindredCouponCoverage.None,
    source: DEFAULT_MANUAL_SOURCE,
    domains: [] as string[],
    countries: [] as string[],
  });
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    form.code.trim() &&
    form.brandId &&
    form.validFrom &&
    form.validTo &&
    !createCoupon.loading;

  const handleCreate = async () => {
    setError(null);
    try {
      const { brandName, ...data } = form;
      const result = await createCoupon.execute("kindred/coupons", {
        data: {
          ...data,
          validFrom: new Date(data.validFrom).toISOString(),
          validTo: new Date(data.validTo).toISOString(),
          summary: data.summary || null,
        },
        comment: comment.trim() || undefined,
      });
      if (result?.id) {
        navigate(`/kindred/coupons/${result.id}`);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to create coupon");
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Create Coupon</h4>
        <Link to="/kindred/coupons">
          <Button size="sm" variant="outline-secondary">
            <ArrowLeft size={14} className="me-1" /> Back to Coupons
          </Button>
        </Link>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small mb-0">
                  Code <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  size="sm"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g. SAVE20"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <BrandTypeahead
                brandId={form.brandId}
                brandName={form.brandName}
                onChange={(brandId, brandName) =>
                  setForm({ ...form, brandId, brandName })
                }
              />
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="small mb-0">Summary</Form.Label>
                <Form.Control
                  as="textarea"
                  size="sm"
                  rows={2}
                  value={form.summary}
                  onChange={(e) =>
                    setForm({ ...form, summary: e.target.value })
                  }
                  placeholder="Brief description of the coupon..."
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-0">
                  Valid From <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={form.validFrom}
                  onChange={(e) =>
                    setForm({ ...form, validFrom: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-0">
                  Valid To <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={form.validTo}
                  onChange={(e) =>
                    setForm({ ...form, validTo: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-0">Published Status</Form.Label>
                <Form.Select
                  size="sm"
                  value={form.publishedStatus}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      publishedStatus: Number(e.target.value),
                    })
                  }
                >
                  {publishedStatusOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-0">Coverage</Form.Label>
                <Form.Select
                  size="sm"
                  value={form.coverage}
                  onChange={(e) =>
                    setForm({ ...form, coverage: Number(e.target.value) })
                  }
                >
                  <option value={KindredCouponCoverage.None}>Not Set</option>
                  <option value={KindredCouponCoverage.SiteWide}>
                    Site-Wide
                  </option>
                  <option value={KindredCouponCoverage.Category}>
                    Category
                  </option>
                  <option value={KindredCouponCoverage.Product}>
                    Product Specific
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small mb-0">Source</Form.Label>
                <SourcePicker
                  value={form.source}
                  onChange={(source) => setForm({ ...form, source })}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-3 g-3">
            <Col md={6}>
              <TagListEditor
                label="Domains"
                normalize="lower"
                items={form.domains}
                onChange={(domains) => setForm({ ...form, domains })}
                placeholder="Add domain..."
              />
            </Col>
            <Col md={6}>
              <TagListEditor
                label="Countries"
                items={form.countries}
                onChange={(countries) => setForm({ ...form, countries })}
                placeholder="Add country code..."
              />
            </Col>
          </Row>

          <hr />

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small mb-0">Comment</Form.Label>
                <Form.Control
                  size="sm"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Optional reason for creating..."
                />
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Button
                variant="primary"
                size="sm"
                disabled={!canSubmit}
                onClick={handleCreate}
              >
                {createCoupon.loading ? (
                  <Spinner animation="border" size="sm" className="me-1" />
                ) : (
                  <Plus size={14} className="me-1" />
                )}
                Create Coupon
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};
